import { createSignal, onCleanup } from "solid-js";
import { useNavigate } from "@solidjs/router";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

export function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => setTimeout(() => reject(new Error(`timeout: ${label}`)), ms)),
  ]);
}

export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(display-mode: standalone)").matches
    || (window.navigator as unknown as { standalone?: boolean }).standalone === true;
}

export function isIos(): boolean {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return /Macintosh/.test(ua) && window.navigator.maxTouchPoints > 1;
}

export function pushSupported(): boolean {
  return typeof navigator !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;
}

export async function getReadyRegistration(timeoutMs = 20000): Promise<ServiceWorkerRegistration> {
  let reg = await navigator.serviceWorker.getRegistration();
  if (!reg) reg = await navigator.serviceWorker.register("/sw.js");
  const registration = reg;
  if (registration.active) return registration;

  const viaState = new Promise<ServiceWorkerRegistration>((resolve, reject) => {
    const track = (w: ServiceWorker | null) => {
      if (!w) return;
      if (w.state === "activated") return resolve(registration);
      w.addEventListener("statechange", () => {
        if (w.state === "activated") resolve(registration);
        else if (w.state === "redundant") reject(new Error("sw-redundant"));
      });
    };
    registration.addEventListener("updatefound", () => track(registration.installing));
    track(registration.installing || registration.waiting);
    if (registration.active) resolve(registration);
  });

  return withTimeout(
    Promise.race([viaState, navigator.serviceWorker.ready]),
    timeoutMs,
    "sw-activate",
  );
}

const [installEvent, setInstallEvent] = createSignal<BeforeInstallPromptEvent | null>(null);
const [installed, setInstalled] = createSignal(isStandalone());
const [pushSubscribed, setPushSubscribed] = createSignal(false);

export { installed, pushSubscribed, setPushSubscribed };

export const canInstall = () => installEvent() !== null;

export const installAvailable = () => !installed() && (canInstall() || (isIos() && !isStandalone()));

export async function promptInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
  const e = installEvent();
  if (!e) return "unavailable";
  await e.prompt();
  const choice = await e.userChoice;
  setInstallEvent(null);
  if (choice.outcome === "accepted") setInstalled(true);
  return choice.outcome;
}

export async function refreshPushSubscribed(): Promise<void> {
  if (!pushSupported() || (isIos() && !isStandalone())) {
    setPushSubscribed(false);
    return;
  }
  try {
    const reg = await getReadyRegistration();
    setPushSubscribed(!!(await reg.pushManager.getSubscription()));
  } catch {
    setPushSubscribed(false);
  }
}

// sw.ts posts the clicked notification's URL here rather than calling client.navigate(), which
// reloads the whole app and is refused outright for a page the worker does not control.
export function useServiceWorkerNavigation(): void {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  const navigate = useNavigate();
  const onMessage = (event: MessageEvent) => {
    if (event.data?.type !== "navigate" || typeof event.data.url !== "string") return;
    const target = new URL(event.data.url, window.location.origin);
    if (target.origin !== window.location.origin) return;
    navigate(target.pathname + target.search + target.hash);
  };
  navigator.serviceWorker.addEventListener("message", onMessage);
  onCleanup(() => navigator.serviceWorker.removeEventListener("message", onMessage));
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    setInstallEvent(e as BeforeInstallPromptEvent);
  });
  window.addEventListener("appinstalled", () => {
    setInstalled(true);
    setInstallEvent(null);
  });
  window.matchMedia("(display-mode: standalone)")
    .addEventListener?.("change", (ev) => { if (ev.matches) setInstalled(true); });
  void refreshPushSubscribed();
}
